/**
 * Frontend shapes of "Acceso MCP" (BR-MCP-01..09). Mirrors
 * `apps/niko-backend-api/src/app/core/dtos/mcp-access-grant.response.dto.ts`
 * and the sibling request DTOs - the backend DTO is the contract, this file
 * only types what the UI reads/sends. Status/step unions are redeclared
 * here rather than imported from `@niko-crm/core` (that alias re-exports
 * TypeORM entities and would pull TypeORM into the browser bundle).
 */

export type McpAccessGrantStatus = 'ACTIVE' | 'REVOKED';
export type McpAccessProvisioningStatus = 'COMPLETE' | 'INCOMPLETE';
export type McpAccessProvisioningStep =
  | 'CREATE_USER'
  | 'GRANT_PERMISSIONS'
  | 'SET_METADATA'
  | 'SEND_INVITATION';
export type McpAccessInvitationDelivery = 'EMAIL' | 'LINK';

export interface McpAccessSlugDrift {
  inSync: boolean;
  /** Granted now, never pushed. */
  missingSlugs: string[];
  /** Pushed, no longer granted. */
  staleSlugs: string[];
  /** Granted tenant ids whose slug is currently NULL - unpushable. */
  tenantsWithoutSlug: string[];
}

export interface McpAccessGrantedTenant {
  id: string;
  name: string;
  slug: string | null;
}

/** Discriminated union - `url` is present exactly once, never persisted (design §3.6). */
export type McpAccessInvitationOutcome =
  | { delivery: 'EMAIL_SENT'; sentAt: string }
  | { delivery: 'LINK'; url: string; expiresAt: string }
  | { delivery: 'FAILED'; errorCode: string }
  | null;

/** Row of `GET /mcp-access` - never the raw entity, never a secret. */
export interface McpAccessGrant {
  id: string;
  email: string;
  displayName: string;
  /** Opaque - support cross-references it in the Auth0 dashboard. */
  auth0UserId: string | null;
  grantedTenants: McpAccessGrantedTenant[];
  pushedTenantSlugs: string[];
  sync: McpAccessSlugDrift;
  status: McpAccessGrantStatus;
  provisioningStatus: McpAccessProvisioningStatus;
  lastProvisioningStep: McpAccessProvisioningStep | null;
  provisioningError: string | null;
  invitationSentAt: string | null;
  invitationDelivery: McpAccessInvitationDelivery | null;
  confirmed: boolean;
  provisionedByName: string;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
  revokedByName: string | null;
  revokeReason: string | null;
  reactivatedAt: string | null;
}

/** Transient fields present only in the response of create / resync / resend-invitation. */
export interface McpAccessMutationResult extends McpAccessGrant {
  adopted?: boolean;
  invitation?: McpAccessInvitationOutcome;
}

export interface CreateMcpAccessGrantPayload {
  email: string;
  displayName: string;
  tenantIds: string[];
  /** Informational only - which `users` row the operator picked, if any. */
  sourceUserId?: string;
}

export interface UpdateMcpAccessTenantsPayload {
  tenantIds: string[];
}

export interface RevokeMcpAccessGrantPayload {
  reason?: string;
}

export interface McpAccessListQuery {
  search?: string;
  status?: McpAccessGrantStatus;
  page: number;
  limit: number;
}

/**
 * `GET /mcp-access`'s envelope - deliberately NOT `PaginatedResult<T>`: the
 * backend list method (design §2.4) returns `{ data, total, page, limit }`
 * with no `totalPages`, unlike `ImpersonationSessionsService`'s equivalent.
 * PrimeNG's paginator only needs `total`, so this costs nothing.
 */
export interface McpAccessListResult {
  data: McpAccessGrant[];
  total: number;
  page: number;
  limit: number;
}

/** Derived client-side display state - first-class filterable per REQ-MCP-05. */
export type McpAccessDisplayStatus = 'active' | 'revoked' | 'incomplete' | 'desynchronized';

export function mcpAccessDisplayStatus(
  grant: Pick<McpAccessGrant, 'status' | 'provisioningStatus' | 'sync'>,
): McpAccessDisplayStatus {
  if (grant.status === 'REVOKED') return 'revoked';
  if (grant.provisioningStatus === 'INCOMPLETE') return 'incomplete';
  if (!grant.sync.inSync) return 'desynchronized';
  return 'active';
}
